import { FolderOpen, Plus } from "lucide-react";
import { useState, useEffect, useMemo } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../../popover";
import { Button } from "../../../button";
import { FileCollectionProps, DetectedFile } from "../../types";
import { isURL, detectFileType, detectFileTypeFromUrl } from "../../utils";
import { AddFilePopover } from "../AddFilePopover";

import { FileList } from "./FileList";

export const FileCollection = ({
  isPopoverOpen,
  setIsPopoverOpen,
  fileCollectionCount,
  detectedFileValues,
  onDeleteFile,
  onAddFiles,
  popoverPortalContainer,
  showDeleteButtons = false,
  saveData,
}: FileCollectionProps) => {
  const [fileTypesMap, setFileTypesMap] = useState<Map<string, DetectedFile | null>>(new Map());

  const fileValueWithLinkTypeInfo = useMemo(
    () =>
      detectedFileValues.map(value => {
        if (isURL(value)) {
          const linkTypeInfo = detectFileTypeFromUrl(value);
          return {
            value,
            linkTypeInfo: linkTypeInfo || undefined,
            detectedFile: fileTypesMap.get(value) || null,
          };
        }
        return {
          value,
          detectedFile: fileTypesMap.get(value) || null,
        };
      }),
    [detectedFileValues, fileTypesMap],
  );

  useEffect(() => {
    const detectAllFileTypes = async () => {
      const newFileTypesMap = new Map<string, DetectedFile | null>();

      await Promise.all(
        detectedFileValues.map(async value => {
          try {
            const fileType = await detectFileType(value.slice(0, 100));
            newFileTypesMap.set(value, fileType);
          } catch {
            newFileTypesMap.set(value, null);
          }
        }),
      );

      setFileTypesMap(newFileTypesMap);
    };

    if (detectedFileValues.length > 0) {
      detectAllFileTypes();
    }
  }, [detectedFileValues]);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="flex h-[36px] w-full items-center justify-start gap-2 px-2 py-4"
        >
          <FolderOpen className="h-5 w-5 text-gray-600" />
          <div className="flex flex-1 items-center justify-between">
            <div className="font-medium text-gray-900">Documents</div>
            <div className="text-xs text-gray-500">{fileCollectionCount} files</div>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-4xl p-4" side="top" align="center">
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <div className="text-sm font-medium text-gray-900">Files ({fileCollectionCount})</div>
            {onAddFiles && (
              <AddFilePopover
                onAddFiles={onAddFiles}
                popoverPortalContainer={popoverPortalContainer}
              >
                <Button variant="ghost" size="sm" className="h-6 p-2 text-xs">
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </AddFilePopover>
            )}
          </div>
          <FileList
            fileValueWithLinkTypeInfo={fileValueWithLinkTypeInfo}
            saveData={saveData}
            onDeleteFile={onDeleteFile}
            showDeleteButtons={showDeleteButtons}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
